import { useEffect, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { LoginPage } from './pages/LoginPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { PhonesPage } from './pages/PhonesPage.js';
import { PhonePage } from './pages/PhonePage.js';
import { ModelsPage } from './pages/ModelsPage.js';
import { ModelPage } from './pages/ModelPage.js';
import { AccountsPage } from './pages/AccountsPage.js';
import { AccountPage } from './pages/AccountPage.js';
import { ArchivePage } from './pages/ArchivePage.js';
import { AnalyticsPage } from './pages/AnalyticsPage.js';
import { ModelAnalyticsPage } from './pages/ModelAnalyticsPage.js';
import { ShutdownPage } from './pages/ShutdownPage.js';
import { CoordinatesPage } from './pages/CoordinatesPage.js';
import { AdminPage } from './pages/AdminPage.js';
import { Sidebar } from './ui/Sidebar.js';
import { ThemeToggle } from './ui/ThemeToggle.js';
import { api, getToken, setToken } from './api/client.js';
import type { User } from '@wml-insta/shared';

const PAGE_TITLES: Array<[RegExp, string]> = [
  [/^\/$/, 'Дашборд'],
  [/^\/phones$/, 'Телефоны'],
  [/^\/phone\//, 'Телефон'],
  [/^\/models$/, 'Модели'],
  [/^\/model\//, 'Модель'],
  [/^\/accounts$/, 'Все аккаунты'],
  [/^\/archive$/, 'Архив аккаунтов'],
  [/^\/account\//, 'Аккаунт'],
  [/^\/admin/, 'Админка'],
];

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const nav = useNavigate();
  const location = useLocation();
  const pageTitle = PAGE_TITLES.find(([re]) => re.test(location.pathname))?.[1] ?? '';

  async function loadMe() {
    const token = getToken();
    if (!token) { setUser(null); setReady(true); return; }
    try {
      const { user } = await api<{ user: User }>('/api/auth/me');
      setUser(user);
    } catch {
      setToken(null);
      setUser(null);
    } finally {
      setReady(true);
    }
  }

  useEffect(() => { loadMe(); }, []);

  if (!ready) return null;
  if (!user) return <LoginPage onLogin={loadMe} />;

  return (
    <div className="app-shell">
      <Sidebar user={user} isSuperAdmin={user.role === 'admin'} />
      <div className="main-area">
        <div className="page-topbar">
          <div className="page-title">{pageTitle}</div>
          <div className="spacer" />
          <ThemeToggle />
          <button
            className="icon-btn"
            title="Выйти"
            onClick={() => { setToken(null); setUser(null); nav('/'); }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <path d="m16 17 5-5-5-5" />
              <path d="M21 12H9" />
            </svg>
          </button>
        </div>

        <Routes>
          <Route path="/" element={<DashboardPage me={user} />} />
          <Route path="/phones" element={<PhonesPage me={user} />} />
          <Route path="/phone/:id" element={<PhonePage me={user} />} />
          <Route path="/models" element={<ModelsPage me={user} />} />
          <Route path="/model/:id" element={<ModelPage me={user} />} />
          <Route path="/accounts" element={<AccountsPage me={user} />} />
          <Route path="/account/:id" element={<AccountPage me={user} />} />
          <Route path="/archive" element={<ArchivePage me={user} />} />
          <Route path="/shutdown" element={<ShutdownPage me={user} />} />
          <Route path="/coordinates" element={<CoordinatesPage me={user} />} />
          <Route path="/analytics" element={user.role === 'admin' ? <AnalyticsPage me={user} /> : <DashboardPage me={user} />} />
          <Route path="/analytics/:id" element={user.role === 'admin' ? <ModelAnalyticsPage /> : <DashboardPage me={user} />} />
          <Route path="/admin" element={<AdminPage me={user} />} />
          <Route path="*" element={<DashboardPage me={user} />} />
        </Routes>
      </div>
    </div>
  );
}
